import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import css from './modal.module.css';

interface Props {
  onClose?: Function;
  children: ReactNode;
  title?: ReactNode;
  description?: string;
  className?: string;
  rootClassName?: string;
  onKeyPress?: (evt: KeyboardEvent) => void;
}

let modalRoot = document.getElementById('modal-root');

if (!modalRoot) {
  modalRoot = document.createElement('div');
  modalRoot.setAttribute('id', 'modal-root');
  document.body.appendChild(modalRoot);
}

const Modal = ({
  title,
  description,
  onClose,
  className,
  rootClassName,
  children,
  onKeyPress,
}: Props) => {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (onClose) {
      const onDocumentKeyUp = (evt: KeyboardEvent) => {
        if (evt.key === 'Escape') onClose?.();
        onKeyPress?.(evt);
      };

      document.addEventListener('keyup', onDocumentKeyUp);

      return () => {
        document.removeEventListener('keyup', onDocumentKeyUp);
      };
    }
  }, [onClose, onKeyPress]);

  return (
    modalRoot &&
    createPortal(
      <div
        className={classNames(css.host, rootClassName)}
        role="dialog"
        data-test-id="modal"
        onClick={() => onClose?.()}
      >
        <div
          className={classNames(css.content, className)}
          ref={hostRef}
          onClick={(evt) => evt.stopPropagation()}
        >
          {onClose && (
            <button
              type="button"
              className={css.closeBtn}
              onClick={() => onClose()}
            />
          )}
          {title && (
            <div className={css.header}>
              {title && <div className={css.title}>{title}</div>}
            </div>
          )}
          {description && <div className={css.description}>{description}</div>}
          {children}
        </div>
      </div>,
      modalRoot
    )
  );
};

export default Modal;
